import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeContactMappingStore,
  BlackbaudRaisersEdgeIntegration,
} from '@feathr/blackbox';
import { toast } from '@feathr/components';

/**
 * Loads (or reloads) the integration's contact mapping store on mount and returns it. Call it from
 * one owner component per page; others read `integration.contactMappingStore` from an `observer`.
 */
function useRaisersEdgeContactMappings(
  integration: BlackbaudRaisersEdgeIntegration,
): BlackbaudRaisersEdgeContactMappingStore {
  const { t } = useTranslation();
  const store = integration.contactMappingStore;

  useEffect(() => {
    let isMounted = true;

    async function load(): Promise<void> {
      await (store.isFetched ? store.refresh() : store.fetch());
      if (isMounted && !store.isFetched && store.error) {
        toast(t("Couldn't load mappings. {{error}}", { error: store.error }), { type: 'error' });
      }
    }

    void load();

    return (): void => {
      isMounted = false;
    };
    // `t` is left out on purpose: a language change must not reload the mappings.
  }, [store]);

  return store;
}

export default useRaisersEdgeContactMappings;
